import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity, AlertTriangle, Wrench, FileWarning, CheckCircle2,
  ChevronDown, ChevronRight, ExternalLink, RefreshCw, Lightbulb,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/shared/api';

/**
 * Diagnostics — "Why are recon jobs failing?"
 *
 * Surfaces the GET /api/admin/diagnostics/failures endpoint, which
 * classifies the last 50 failed jobs into a small set of canonical
 * buckets with a technical-vs-functional verdict and a remediation hint
 * for each. The page is built around one question: should the operator
 * file a bug, or edit a config?
 */

type FailureBucket =
  | 'portfolio_parse' | 'dp_extraction' | 'schema_validation'
  | 'claude_api' | 'cancelled_by_user' | 'database'
  | 'auth_or_config' | 'unknown';

interface FailureGroup {
  bucket: FailureBucket;
  subcategory: string;
  is_technical: boolean;
  remediation: string;
  count: number;
  sample_error: string;
  sample_job_id: string;
}

interface FailureRow {
  id: string;
  status: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  duration_ms: number;
  bucket: FailureBucket;
  subcategory: string;
  is_technical: boolean;
  remediation: string;
}

interface FailuresResponse {
  total: number;
  technical_count: number;
  functional_count: number;
  groups: FailureGroup[];
  failures: FailureRow[];
}

const BUCKET_LABEL: Record<FailureBucket, string> = {
  portfolio_parse:   'Portfolio Excel parsing',
  dp_extraction:     'DP statement extraction',
  schema_validation: 'Schema validation',
  claude_api:        'Claude API',
  cancelled_by_user: 'Cancelled by user',
  database:          'Database / infrastructure',
  auth_or_config:    'Auth / configuration',
  unknown:           'Unclassified',
};

const BUCKET_TONE: Record<FailureBucket, 'err' | 'warn' | 'info' | 'ok' | 'neutral'> = {
  portfolio_parse:   'warn',
  dp_extraction:     'err',
  schema_validation: 'err',
  claude_api:        'err',
  cancelled_by_user: 'ok',
  database:          'err',
  auth_or_config:    'err',
  unknown:           'neutral',
};

export function DiagnosticsPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'diagnostics', 'failures'],
    queryFn: () => api.get<FailuresResponse>('/api/admin/diagnostics/failures').then(r => r.data),
    staleTime: 30_000,
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-ink-strong inline-flex items-center gap-2">
            <Activity className="w-7 h-7" style={{ color: 'var(--accent)' }} />
            Diagnostics
          </h1>
          <p className="text-sm text-ink-400 mt-1.5 tracking-tight max-w-3xl">
            The last 50 failed reconciliation jobs, classified into canonical failure modes with remediation hints. Each row is tagged <strong>technical</strong> (a bug in our code or infrastructure) or <strong>functional</strong> (input data shape that needs a config tweak).
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition disabled:opacity-50"
          style={{
            background: 'var(--bg-hover)',
            color: 'var(--text-strong)',
            border: '1px solid var(--glass-border-h)',
          }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {isLoading && (
        <div className="card p-12 text-center text-ink-400">Loading…</div>
      )}

      {data && (
        <>
          {/* Top-level summary tiles */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <SummaryTile
              label="Total failures"
              value={data.total}
              icon={<AlertTriangle className="w-5 h-5" />}
              tone="neutral"
            />
            <SummaryTile
              label="Technical (bug)"
              value={data.technical_count}
              icon={<Wrench className="w-5 h-5" />}
              tone="err"
              hint="These need code fixes or infra checks."
            />
            <SummaryTile
              label="Functional (data)"
              value={data.functional_count}
              icon={<FileWarning className="w-5 h-5" />}
              tone="warn"
              hint="These are input shape mismatches — fix via Prompts page configs."
            />
          </div>

          {data.total === 0 ? (
            <div className="card p-12 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--status-ok)' }} />
              <div className="text-lg font-semibold text-ink-strong mb-1">No failed jobs in the last 50</div>
              <div className="text-sm text-ink-400">Reconciliation pipeline is healthy.</div>
            </div>
          ) : (
            <>
              {/* Grouped patterns */}
              <h2 className="text-sm uppercase tracking-[0.18em] text-ink-400 mb-3">Failure patterns (most common first)</h2>
              <div className="space-y-3 mb-8">
                {data.groups.map((g, i) => <GroupCard key={i} group={g} />)}
              </div>

              {/* Drill-down: full failure list */}
              <h2 className="text-sm uppercase tracking-[0.18em] text-ink-400 mb-3">All failures (most recent first)</h2>
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <thead style={{ background: 'var(--bg-hover)', borderBottom: '1px solid var(--glass-border)' }}>
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-400">
                      <th className="px-4 py-3">Job</th>
                      <th className="px-4 py-3">Failure</th>
                      <th className="px-4 py-3">Kind</th>
                      <th className="px-4 py-3 text-right">Ran for</th>
                      <th className="px-4 py-3">Failed at</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.failures.map((f) => <FailureRowView key={f.id} failure={f} />)}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function SummaryTile({
  label, value, icon, tone, hint,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: 'err' | 'warn' | 'ok' | 'neutral';
  hint?: string;
}) {
  const toneVar = {
    err: 'var(--status-err)',
    warn: 'var(--status-warn)',
    ok: 'var(--status-ok)',
    neutral: 'var(--text-strong)',
  }[tone];
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3 mb-1.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
             style={{ background: `color-mix(in srgb, ${toneVar} 12%, transparent)`, color: toneVar }}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400">{label}</div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: toneVar }}>{value}</div>
        </div>
      </div>
      {hint && <div className="text-xs text-ink-400 mt-1">{hint}</div>}
    </div>
  );
}

function GroupCard({ group }: { group: FailureGroup }) {
  const [open, setOpen] = useState(false);
  const tone = BUCKET_TONE[group.bucket];
  const toneVar = {
    err: 'var(--status-err)',
    warn: 'var(--status-warn)',
    info: 'var(--status-info)',
    ok: 'var(--status-ok)',
    neutral: 'var(--text-400)',
  }[tone];

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-4 flex items-start gap-3 text-left transition"
        style={{ background: open ? 'var(--bg-hover)' : 'transparent' }}
      >
        <div className="mt-0.5 text-ink-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="pill" style={{
              background: `color-mix(in srgb, ${toneVar} 12%, transparent)`,
              borderColor: `color-mix(in srgb, ${toneVar} 40%, transparent)`,
              color: toneVar,
            }}>
              {BUCKET_LABEL[group.bucket]}
            </span>
            <span className="text-xs text-ink-400 font-mono">{group.subcategory}</span>
            <span className="pill" style={{
              background: group.is_technical
                ? 'color-mix(in srgb, var(--status-err) 12%, transparent)'
                : 'color-mix(in srgb, var(--status-warn) 12%, transparent)',
              borderColor: group.is_technical
                ? 'color-mix(in srgb, var(--status-err) 40%, transparent)'
                : 'color-mix(in srgb, var(--status-warn) 40%, transparent)',
              color: group.is_technical ? 'var(--status-err)' : 'var(--status-warn)',
            }}>
              {group.is_technical ? 'Technical' : 'Functional'}
            </span>
          </div>
          <div className="text-sm text-ink-strong tracking-tight truncate">{group.remediation}</div>
        </div>
        <div className="text-right text-sm font-bold tabular-nums shrink-0 ml-3" style={{ color: toneVar }}>
          {group.count}
          <div className="text-[10px] text-ink-400 font-normal">{group.count === 1 ? 'job' : 'jobs'}</div>
        </div>
      </button>
      {open && (
        <div className="px-5 py-4 space-y-3" style={{ borderTop: '1px solid var(--glass-border)', background: 'var(--bg-input)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">Sample error</div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-words p-3 rounded-lg"
                 style={{ background: 'var(--bg-hover)', color: 'var(--text-50)' }}>
              {group.sample_error}
            </pre>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1 inline-flex items-center gap-1">
              <Lightbulb className="w-3 h-3" />
              Recommended action
            </div>
            <div className="text-sm text-ink-strong leading-relaxed">{group.remediation}</div>
          </div>
          <div>
            <Link to={`/recon/${group.sample_job_id}`}
                  className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded transition"
                  style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
              View sample job <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function FailureRowView({ failure }: { failure: FailureRow }) {
  const tone = BUCKET_TONE[failure.bucket];
  const toneVar = {
    err: 'var(--status-err)',
    warn: 'var(--status-warn)',
    info: 'var(--status-info)',
    ok: 'var(--status-ok)',
    neutral: 'var(--text-400)',
  }[tone];
  const durationSec = (failure.duration_ms / 1000).toFixed(1);
  return (
    <tr style={{ borderBottom: '1px solid var(--glass-border)' }} className="hover:bg-[var(--bg-hover)]">
      <td className="px-4 py-3 font-mono text-xs text-ink-400">{failure.id.slice(0, 12)}…</td>
      <td className="px-4 py-3">
        <div className="text-sm text-ink-strong truncate max-w-xl">{failure.error_message ?? <em>(no message)</em>}</div>
        <div className="text-[10px] text-ink-400 font-mono mt-0.5">{failure.subcategory}</div>
      </td>
      <td className="px-4 py-3">
        <span className="pill" style={{
          background: `color-mix(in srgb, ${toneVar} 12%, transparent)`,
          borderColor: `color-mix(in srgb, ${toneVar} 40%, transparent)`,
          color: toneVar,
        }}>
          {BUCKET_LABEL[failure.bucket]}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-xs font-mono tabular-nums text-ink-400">{durationSec}s</td>
      <td className="px-4 py-3 text-xs text-ink-400 tabular-nums">
        {format(new Date(failure.updated_at), 'd MMM, HH:mm:ss.SSS')}
      </td>
      <td className="px-4 py-3 text-right">
        <Link to={`/recon/${failure.id}`} className="text-xs inline-flex items-center gap-1"
              style={{ color: 'var(--accent)' }}>
          View <ExternalLink className="w-3 h-3" />
        </Link>
      </td>
    </tr>
  );
}
